// Dates and numbers for both apps (design-system.md §6): Intl in the user's time zone, unambiguous absolute dates
// ("28 Sept 2026"), relative ages in the queues ("4 days ago"), amounts always with the currency code.

const LOCALE = "en-GB";

const dateFormat = new Intl.DateTimeFormat(LOCALE, { day: "numeric", month: "short", year: "numeric" });
const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** `2026-09-28` (a DB date) or an ISO timestamp → "28 Sept 2026". A date without time is not shifted by the zone. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  return dateFormat.format(date);
}

/** The day of an instant in the user's calendar, as a DB date and an `<input type="date">` write it: `2026-11-01`. */
export function localDay(at: Date = new Date()): string {
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(at.getDate()).padStart(2, "0")}`;
}

/** A window of two dates ("1 Jun 2026 – 28 Jun 2026"); empty when both are missing. */
export function formatDateRange(from: string | null | undefined, to: string | null | undefined): string {
  return from || to ? `${formatDate(from)} – ${formatDate(to)}` : "";
}

const timeFormat = new Intl.DateTimeFormat(LOCALE, { timeStyle: "short" });

/** "10:42": when a number shown on screen was read (design-system.md §4.7). */
export function formatTime(value: number | string | Date): string {
  return timeFormat.format(new Date(value));
}

/** "1 Jun 2026 10:42": an instant, where the day alone does not say enough (a history entry, a scheduled change). */
export function formatDateTime(value: string): string {
  return `${formatDate(value)} ${formatTime(value)}`;
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "4 days ago", "in 2 hours", "now": for ages in queues and "waiting since". */
export function formatAge(value: string | null | undefined, now: number = Date.now()): string {
  if (!value) return "";
  const seconds = Math.round((new Date(value).getTime() - now) / 1000);
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return relative.format(Math.trunc(seconds / size), unit);
  }
  return relative.format(0, "second");
}

const moneyFormats = new Map<string, Intl.NumberFormat>();

const kilobytes = new Intl.NumberFormat(LOCALE, { style: "unit", unit: "kilobyte", maximumFractionDigits: 0 });
const megabytes = new Intl.NumberFormat(LOCALE, { style: "unit", unit: "megabyte", maximumFractionDigits: 1 });

/** The size of a file → "840 kB", "4.2 MB" (1 kB = 1,000 bytes, as browsers and file managers say). */
export function formatBytes(bytes: number): string {
  return bytes < 1_000_000 ? kilobytes.format(Math.max(1, Math.round(bytes / 1000))) : megabytes.format(bytes / 1e6);
}

/** A DB numeric (string) with its currency → "EUR 1,240.00"; empty when there is no amount. */
export function formatMoney(amount: string | number | null | undefined, currency: string): string {
  if (amount === null || amount === undefined || amount === "") return "";
  let format = moneyFormats.get(currency);
  if (!format) {
    format = new Intl.NumberFormat(LOCALE, { style: "currency", currency, currencyDisplay: "code" });
    moneyFormats.set(currency, format);
  }
  return format.format(Number(amount)).replace(/ /g, " ");
}

/** A DB numeric ("135.6000") as the user would type it ("135.6"): what a form or a cell starts from. */
export function plainDecimal(value: string | null | undefined): string {
  return value === null || value === undefined || value === "" ? "" : String(Number(value));
}

/**
 * What a user types for an amount or a percentage → the decimal text the API takes ("12,5" → "12.5"), or null if it
 * is not a number, is negative or has more decimals than `scale`.
 */
export function parseDecimal(text: string, scale = 4): string | null {
  const value = text.trim().replace(",", ".");
  const [, whole, fraction = ""] = /^(\d+)(?:\.(\d+))?$/.exec(value) ?? [];
  if (whole === undefined || fraction.length > scale) return null;
  return value;
}

/** A change of units with its sign, the minus as a typographic one: `+3`, `−1`. */
export const formatSigned = (units: number): string => (units < 0 ? `−${-units}` : `+${units}`);

/** What a user types for a number of units → the number, or null if it is not a whole number of zero or more. */
export function parseUnits(text: string): number | null {
  const value = text.trim();
  return /^\d+$/.test(value) ? Number(value) : null;
}
